/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';

import { PageHeader } from '@features/property-management/components/PageHeader';
import { DataTable } from '@features/property-management/components/DataTable';
import { VisitsKanbanBoard } from '../components/VisitsKanbanBoard';
import { VisitsMobileCardView } from '../components/VisitsMobileCardView';
import { VisitFilterPanel } from '../components/VisitFilterPanel';
import { VisitDrawer } from '@/features/visits/components/VisitDrawer';
import { KanbanSkeleton, Text } from '@/shared/ui';

import { VisitsToolbar } from './ManageVisitsPage.toolbar';
import { VisitFeedbackDialog, VisitCancelDialog } from './ManageVisitsPage.dialogs';
import { useManageVisits } from './ManageVisitsPage.useManageVisits';
import { VisitDetailOverlay } from '@pages/authenticated/agency-pages/visits/VisitDetailOverlay';

const SK = 'animate-pulse rounded-(--radius-md) bg-[var(--skeleton-base)]';

/**
 * Loading placeholder for the visits list header.
 */
function VisitsHeaderSkeleton() {
  return (
    <div style={{ flexShrink: 0, padding: 'var(--space-4) var(--space-5) 0', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div
        style={{
          padding: 'var(--space-4) var(--space-5)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-subtle)',
          background: 'var(--surface-primary)',
          boxShadow: 'var(--shadow-md)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className={`${SK} h-7 w-32`} />
          <div className={`${SK} h-9 w-32`} />
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <div className={`${SK} h-10 flex-1`} />
          <div className={`${SK} h-10 w-24`} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <div className={`${SK} h-9 w-48`} />
        <div className={`${SK} h-9 w-24`} />
      </div>
    </div>
  );
}

const ManageVisitsPage: React.FC = () => {
  const vm = useManageVisits();
  const { t } = vm;
  const { t: tVisits } = useTranslation('features/visits');

  const handleVisitClick = (id: string | null) => {
    if (id) vm.openDetail(id);
  };

  return (
    <div
      style={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface-primary)',
        fontFamily: 'var(--font-family)',
      }}
    >
      {vm.isLoading ? (
        <VisitsHeaderSkeleton />
      ) : (
      <div style={{ flexShrink: 0, padding: 'var(--space-4) var(--space-5) 0' }}>
        <PageHeader
          title={t('visits.title')}
          icon={<MapPin width={20} height={20} />}
          ctaLabel={t('visits.newVisit')}
          searchPlaceholder={t('visits.searchPlaceholder')}
          onCtaClick={vm.handleNewVisit}
          onSearchChange={vm.setSearch}
          onFiltersToggle={() => vm.setFiltersOpen((o) => !o)}
          activeFilters={vm.activeFilterLabels}
          onRemoveFilter={vm.handleRemoveFilter}
          onClearAllFilters={vm.handleClearAll}
          filterCount={vm.filterCount}
          introLabel={tVisits('intro.label')}
          introBody={tVisits('intro.body')}
          introTestIdPrefix="visits"
        />

        <VisitsToolbar
          viewMode={vm.viewMode}
          onViewModeChange={vm.setViewMode}
          mobileKanbanLayout={vm.mobileKanbanLayout}
          onMobileKanbanLayoutChange={vm.setMobileKanbanLayout}
        />
      </div>
      )}

      {/* Content (owns its own vertical scroll; the page fills its layout pane) */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 var(--space-5) var(--space-5)' }}>
        {vm.isLoading ? (
          <div style={{ paddingTop: 'var(--space-8)', paddingBottom: 'var(--space-8)' }}>
            <KanbanSkeleton />
          </div>
        ) : vm.viewMode === 'kanban' ? (
          <VisitsKanbanBoard
            visits={vm.filteredVisits}
            selectedId={vm.selectedId}
            onSelectVisit={handleVisitClick}
            onStatusChange={vm.handleStatusChange}
            mobileLayout={vm.mobileKanbanLayout}
          />
        ) : vm.filteredVisits.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-12) var(--space-4)',
              borderRadius: 'var(--radius-lg)',
              border: '1.5px dashed var(--border-subtle)',
              background: 'var(--surface-secondary)',
              marginTop: 'var(--space-4)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'color-mix(in srgb, var(--text-tertiary) 8%, transparent)',
              }}
            >
              <MapPin width={22} height={22} style={{ color: 'var(--text-tertiary)', opacity: 0.6 } as React.CSSProperties} />
            </div>
            <Text style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-tertiary)', fontWeight: 'var(--font-medium)' as unknown as number }}>
              {t('visits.kanban.noVisits')}
            </Text>
          </div>
        ) : (
          <>
            <div className="hidden lg:block">
              <DataTable
                data={vm.filteredVisits}
                columns={vm.tableColumns}
                onRowClick={(v) => handleVisitClick(v.id)}
              />
            </div>
            <div className="block lg:hidden">
              <VisitsMobileCardView
                visits={vm.filteredVisits}
                selectedId={vm.selectedId}
                onCardClick={(id) => handleVisitClick(id)}
              />
            </div>
          </>
        )}
      </div>

      {/* Filter panel */}
      <VisitFilterPanel
        open={vm.filtersOpen}
        onClose={() => vm.setFiltersOpen(false)}
        onApply={(f) => vm.setFilters(f)}
        onClear={vm.handleClearAll}
        initialFilters={vm.filters}
        // A solo broker has a single member, so a broker filter would be a
        // one-option no-op: the empty list hides the whole section.
        members={vm.isSoloBroker ? [] : (vm.agency as any)?.members || []}
        periodOptions={vm.periodOptions}
      />

      {/* Visit Drawer */}
      <VisitDrawer
        open={vm.drawerOpen}
        onClose={() => { vm.setDrawerOpen(false); vm.setEditingVisit(null); }}
        onSuccess={() => { vm.setDrawerOpen(false); vm.setEditingVisit(null); vm.refetch(); }}
        agencyId={vm.agency?.id || ''}
        userEmail={vm.user?.profile?.email || ''}
        editingVisit={vm.editingVisit?.raw}
        members={(vm.agency as any)?.members || []}
      />

      <VisitFeedbackDialog
        open={vm.feedbackDialogOpen}
        onOpenChange={vm.setFeedbackDialogOpen}
        rating={vm.feedbackRating}
        onRatingChange={vm.setFeedbackRating}
        obs={vm.feedbackObs}
        onObsChange={vm.setFeedbackObs}
        onConfirm={vm.handleConfirmFeedback}
        t={t}
      />

      <VisitCancelDialog
        open={vm.cancelDialogOpen}
        onOpenChange={vm.setCancelDialogOpen}
        reason={vm.cancelReason}
        onReasonChange={vm.setCancelReason}
        onConfirm={vm.handleConfirmCancel}
        t={t}
      />

      <VisitDetailOverlay
        visitId={vm.detailVisitId}
        agencyId={vm.agency?.id || ''}
        members={(vm.agency as any)?.members || []}
        onClose={vm.closeDetail}
        onVisitUpdated={vm.refetch}
      />
    </div>
  );
};

export default ManageVisitsPage;
